import { h, Component } from 'preact';
import Provider from  'preact-context-provider';
import { TrackingProvider, trackingProvider, DIMENSION } from '@cube/track';
import cx from 'classnames';
import { Row } from '@cube/layout';
import { ScrollDirection } from '@cube/blocks';
import { DropDownSearch } from '@cube/search-input';
import LinkEvent, { linkEventLabel, linkEventIcon, linkEventImage } from '../../../linkevent';
import { withConfig } from '../../../../config';
import style from './style.less';
import WidthConstrain from '../../../widthconstrain';
import LogoIcon from '../../../logoicon';
import { openSearchInNewWindow } from '../../../../lib';

// Custom P25 dropdown renderer that handles dividers
function P25Dropdown({ item, isOpen, onItemClick }) {
	if (!item.subnav || !isOpen) return null;

	const renderSubnav = (subnav) => subnav.map((subItem, index) => {
		if (subItem.divider) {
			return <div key={`divider-${index}`} class={style.p25Divider} />;
		}

		const linkProps = {
			href: subItem.url,
			class: style.p25MenuLink,
			onClick: onItemClick
		};

		if (subItem.event === 'external') {
			linkProps.target = '_blank';
			linkProps.rel = 'noopener noreferrer';
		}

		if (subItem.subnav && subItem.subnav.length > 0) {
			return (
				<div key={index} class={cx(style.p25MenuItem, style.hasSubmenu)}>
					<a {...linkProps}>{subItem.label}</a>
					<div class={style.p25Submenu}>
						{renderSubnav(subItem.subnav)}
					</div>
				</div>
			);
		}

		return (
			<div key={index} class={style.p25MenuItem}>
				<a {...linkProps}>{subItem.label}</a>
			</div>
		);
	});

	return (
		<div class={style.p25DropdownContainer}>
			{renderSubnav(item.subnav)}
		</div>
	);
}

// Custom P25 client links component
class P25ClientLinks extends Component {
	state = {
		openDropdown: null
	};

	leaveTimer = null;

	handleMouseEnter = (index) => {
		clearTimeout(this.leaveTimer);
		this.setState({ openDropdown: index });
		if (this.props.onMenuOpen) {
			this.props.onMenuOpen(true, this.props.clientLinks[index], { type: 'hover', index }, false, 'P25ClientLinks');
		}
	}

	handleMouseLeave = () => {
		this.leaveTimer = setTimeout(() => {
			const { openDropdown } = this.state;
			if (openDropdown !== null && this.props.onMenuOpen) {
				this.props.onMenuOpen(false, this.props.clientLinks[openDropdown], { type: 'hover', index: openDropdown }, false, 'P25ClientLinks');
			}
			this.setState({ openDropdown: null });
		}, 200);
	}

	handleItemClick = () => {
		this.setState({ openDropdown: null });
	}

	handlePreventDefault = (e) => {
		e.preventDefault();
	}

	createMouseEnterHandler = (index) => () => {
		this.handleMouseEnter(index);
	}

	render({ clientLinks }) {
		const { openDropdown } = this.state;

		return (
			<div class={style.clientLinks}>
				{clientLinks.map((link, index) => (
					<div
						key={index}
						class={style.p25ClientLink}
						onMouseEnter={this.createMouseEnterHandler(index)}
						onMouseLeave={this.handleMouseLeave}
					>
						<div class={style.linkWrapper}>
							<a
								href="#"
								class={cx(style.userLink, style.withArrow)}
								onClick={this.handlePreventDefault}
							>
								<span>{link.label}</span>
							</a>
						</div>
						<P25Dropdown
							item={link}
							isOpen={openDropdown === index}
							onItemClick={this.handleItemClick}
						/>
					</div>
				))}
			</div>
		);
	}
}

function PrimaryHeader({
	theme,
	hideLogo,
	logo,
	nonClientLinks,
	clientLinks,
	class: cls,
	forceFocus,
	useLargeLogo,
	searchColorTheme,
	showWideView,
	portalStaticOrigin,
	defaultProvider,
	navType,
	onMenuOpen,
	pulseOnFocus,
	onSearchBlur
}) {
	let context = 'home';

	return (
		<Row class={cx(style.primary, style[theme], cls)}>
			<WidthConstrain class={style.widthConstrain} showWideView={showWideView}>
				{ !hideLogo &&
					<div class={style.logoContainer}>
						<LogoIcon
							theme={theme}
							size="lg"
							link={logo}
							class={cx(style.logo, useLargeLogo && style.large)}
							portalStaticOrigin={portalStaticOrigin}
						/>
					</div>
				}
				<TrackingProvider {...{
					[DIMENSION.text]: searchColorTheme
				}}
				>
					<div class={cx(style.searchContainer, style[theme], style[searchColorTheme])}>
						<DropDownSearch
							class={cx(style.search)}
							context={context}
							buttonClass={cx(style.searchButton)}
							inputTextClass={cx(style.searchInput, pulseOnFocus && style.pulseOnFocus)}
							forceFocus={forceFocus}
							openSearchInNewWindow={openSearchInNewWindow()}
							defaultProvider={defaultProvider}
							onBlur={onSearchBlur}
							clearTextClass={style.clearMe}
						/>
					</div>
				</TrackingProvider>

				{/* Primary header navigation-style links */}
				<div class={cx(style.primaryNavLinks)}>
					{
						nonClientLinks && nonClientLinks.length > 0 &&
						<TrackingProvider {...{
							[DIMENSION.componentpath]: 'NonClientLinks'
						}}
						>
							<div class={cx(style.nonClientLinks)}>
								{nonClientLinks.map((link, index) => (
									<LinkEvent
										key={index}
										class={cx(style.nonClientLink, style[theme])}
										link={link}
									>
										{linkEventImage(link, { theme, class: style.linkRendererImage })}
										{linkEventIcon(link, { theme, class: style.linkRendererIcon })}
										{linkEventLabel(link, { theme, class: style.linkRendererLabel })}
									</LinkEvent>
								))}
							</div>
						</TrackingProvider>
					}
					{
						clientLinks && clientLinks.length > 0 &&
						<TrackingProvider {...{
							[DIMENSION.componentpath]: 'ClientLinks'
						}}
						>
							<P25ClientLinks
								clientLinks={clientLinks}
								onMenuOpen={onMenuOpen}
							/>
						</TrackingProvider>
					}
				</div>
			</WidthConstrain>
		</Row>
	);
}

@trackingProvider({
	[DIMENSION.componentpath]: 'DesktopHeaderP25'
})
export class DesktopHeaderP25 extends Component {

	state = {
		navMenuIsOpen: false,
		scrollDirectionVertical: 0,
		blockFixedTop: false,
		specialLinks: [],
		scrollPosition: 0,
		searchColorTheme: 'colorScheme1',
		pulseOnFocus: true
	};

	handleMenuOpen = (isOpen, link, meta, isSubMenu, incomingContext) => {
		this.setState({
			navMenuIsOpen: isOpen
		});
	}

	handleSearchBlur = () => {
		this.setState({ pulseOnFocus: false });
	}

	handleDirectionChange = e => {
		this.setState({ scrollDirectionVertical: e.detail.direction });
	}

	handleOffsetForFixedToTop = pos => {
		this.setState({
			scrollPosition: pos
		});
	}

	capturePlaceholderRef = ref => {
		this.placeholderRef = ref;
	}

	static defaultProps = {
		displayPrimary: true,
		displayTopHat: false,
		displayNavigation: false,
		fixedToTop: true,
		placeholdHeight: true,
		theme: 'dark',
		allowHideNavigation: false
	};

	render({
		theme, fixedToTop, placeholdHeight,
		displayPrimary, hideLogo,
		...props
	},
	{
		navMenuIsOpen, scrollDirectionVertical, blockFixedTop, scrollPosition, searchColorTheme,
		pulseOnFocus
	}
	) {
		fixedToTop = blockFixedTop ? false : fixedToTop;
		let hasScrolledVertically = scrollPosition > 90;

		let header = (
			<header class={cx(style.header, fixedToTop && style.fixedToTop, hasScrolledVertically && style.scrolled, style[theme])}>
				<ScrollDirection
					debounce={1}
					onDirectionChange={this.handleDirectionChange}
					onScroll={this.handleOffsetForFixedToTop}
				/>

				{displayPrimary && (
					<TrackingProvider {...{
						[DIMENSION.componentpath]: 'P25PrimaryHeader'
					}}
					>
						<PrimaryHeader
							theme={theme}
							hideLogo={hideLogo && !hasScrolledVertically}
							window={window}
							searchColorTheme={searchColorTheme}
							onMenuOpen={this.handleMenuOpen}
							pulseOnFocus={pulseOnFocus}
							onSearchBlur={this.handleSearchBlur}
							{...props}
						/>
					</TrackingProvider>
				)}
			</header>
		);

		if (placeholdHeight) {
			header = (
				<div
					ref={this.capturePlaceholderRef}
					class={cx(
						style.headerPlaceholder,
						fixedToTop && style.fixedToTop,
						hasScrolledVertically && style.scrolled
					)}
				>
					{ header }
				</div>
			);
		}

		return (
			<Provider theme={theme}>{header}</Provider>
		);
	}
}

const Header = withConfig([
	'portalStaticOrigin',

	'headerWidgetP25.theme',

	'headerWidgetP25.desktop.logo',
	'headerWidgetP25.logo',

	'headerWidgetP25.desktop.useLargeLogo',
	'headerWidgetP25.desktop.hideLogo',

	'headerWidgetP25.desktop.displayPrimary',

	'headerWidgetP25.desktop.clientLinks',
	'headerWidgetP25.clientLinks',

	'headerWidgetP25.desktop.nonClientLinks',
	'headerWidgetP25.nonClientLinks',

	'headerWidgetP25.desktop.navType',

	'headerWidgetP25.desktop.fixedToTop',
	'headerWidgetP25.fixedToTop',

	'headerWidgetP25.desktop.placeholdHeight',
	'headerWidgetP25.placeholdHeight',

	'headerWidgetP25.desktop.search.forceFocus',
	'headerWidgetP25.search.forceFocus'
])(
	({ layout, ...props }) => <DesktopHeaderP25 data-type="component" {...props} />
);

export default Header;